import { useEffect, useState } from 'react';

export type RecommendedSectionMode = 'loading' | 'landed' | 'none';

interface UseRecommendedSectionVisibilityArgs {
    /** What the Recommended section currently is: a loading placeholder, the landed rec rows, or absent. */
    mode: RecommendedSectionMode;
    /** The Virtuoso scroller element (from `scrollerRef`) — the IntersectionObserver root. */
    scrollerEl: HTMLElement | null;
    /** The inline gathering-placeholder element (from the Header) — observed while `loading`. */
    headerEl: HTMLElement | null;
    /** Count of RECOMMENDED items (the first group) — used to find the 50% point while `landed`. */
    recommendedCount: number;
    /** First visible item index (from `rangeChanged`) — used while `landed`. */
    visibleStartIndex: number;
}

/**
 * Whether the Recommended section is more than half scrolled out of view (the peek-chip trigger,
 * symmetric at 50%). Two mechanisms because the section is a single element while loading but
 * virtualized rows once landed:
 *  - `loading`: an `IntersectionObserver(0.5)` rooted at the scroller, observing the gathering
 *    placeholder. Re-observes automatically when the placeholder remounts (headerEl changes).
 *  - `landed`: `rangeChanged` — out of view once the first visible item is at or past half the
 *    recommended count (the recommended rows are indices 0..count-1, the first group).
 * Returns `{ outOfView: false }` when there is no section to peek at.
 */
export function useRecommendedSectionVisibility({
    mode,
    scrollerEl,
    headerEl,
    recommendedCount,
    visibleStartIndex,
}: UseRecommendedSectionVisibilityArgs): { outOfView: boolean } {
    const [headerOutOfView, setHeaderOutOfView] = useState(false);

    useEffect(
        function observeGatheringPlaceholder() {
            if (mode !== 'loading' || !scrollerEl || !headerEl) {
                return undefined;
            }
            const observer = new IntersectionObserver(
                function onIntersection([entry]) {
                    setHeaderOutOfView(entry.intersectionRatio < 0.5);
                },
                { root: scrollerEl, threshold: [0, 0.5, 1] }
            );
            observer.observe(headerEl);
            return function disconnect() {
                observer.disconnect();
            };
        },
        [mode, scrollerEl, headerEl]
    );

    if (mode === 'loading') {
        return { outOfView: headerOutOfView };
    }
    if (mode === 'landed' && recommendedCount > 0) {
        return { outOfView: visibleStartIndex >= Math.ceil(recommendedCount / 2) };
    }
    return { outOfView: false };
}
